import { makeAutoObservable, reaction } from "mobx";
import { RootStore } from "./RootStore";
import {
  CirclePlayer,
  CirclePlayersList,
  Player,
  PlayerSelectionBottomSheetType,
} from "../apis/types";
import { CourtBookingApi } from "../apis/bookingapi";
import { removeDuplicates } from "../utils/utils";
import { GUEST_PLAYERS, TOTAL_PLAYERS_IN_MATCH_SINGLES } from "../utils/data";
import { Strings } from "../utils/strings";
import { CourtId } from "../types/types";

export class SelectCircleStore {
  selectedCircles: number[] = [];
  isInitialExpanded: boolean = true;
  rootStore: RootStore;
  playersListResponse: Player[] | null = null;
  searchPlayerListResponse: Player[] | null = null;
  isLoading: boolean = false;
  pageNum: number = 0;
  searchPageNum: number = 0;
  filteredName: string = "";
  selectedPlayers: Player[] = []; // New field for selected players
  excludedPlayers: Player[] = []; // New field for excluded players
  circlePlayerResponse: CirclePlayersList | null = null;
  initialCirclePlayersList: CirclePlayer[] = [];
  isStopPagination: boolean = false;
  isStopSearchPagination: boolean = false;
  bottomSheetType: PlayerSelectionBottomSheetType =
    PlayerSelectionBottomSheetType.none;
  isCirclePlayersLoading: boolean = false;
  isNextButtonDisabled: boolean = true;
  isCircleSelectionDisabled: boolean = false;
  guestPlayerCount: number = 0;
  isAIWillSelectPlayerModalVisible: boolean = false;
  isShowRemoveExtraPlayersDialog: boolean = false;

  // New field to remove selected players if singles tab is selected
  selectedPlayersToRemove: Player[] = [];

  // Reset all store values to their initial state
  resetStore = () => {
    this.selectedCircles = [];
    this.isInitialExpanded = true;
    this.searchPlayerListResponse = null;
    this.isLoading = false;
    this.pageNum = 0;
    this.searchPageNum = 0;
    this.filteredName = "";
    this.selectedPlayers = [];
    this.excludedPlayers = [];
    this.isStopPagination = false;
    this.isStopSearchPagination = false;
    this.bottomSheetType = PlayerSelectionBottomSheetType.none;
    this.isNextButtonDisabled = true;
    this.isCircleSelectionDisabled = false;
    this.guestPlayerCount = 0;
    this.isAIWillSelectPlayerModalVisible = false;
    this.isShowRemoveExtraPlayersDialog = false;
    this.selectedPlayersToRemove = [];
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    reaction(
      () => ({
        page: this.searchPageNum,
        filterName: this.filteredName,
      }),
      async (current, previous) => {
        if (current.filterName === "") {
          this.searchPlayerListResponse = null;
          return;
        }
        if (!previous || current.filterName !== previous.filterName) {
          this.searchPageNum = 0;
          this.isStopSearchPagination = false;
          this.searchPlayerListResponse = null;
        }
        await this.fetchFilteredPlayers();
      }
    );

    reaction(
      () => ({
        page: this.pageNum,
      }),
      async (current, previous) => {
        await this.fetchPlayers();
      }
    );

    // TODO: uncomment this code when we enable select circle graph
    // reaction(
    //   () => ({
    //     selectedPlayersLength: this.selectedPlayers.length,
    //     selectedCirclesLength: this.selectedCircles.length,
    //   }),
    //   async (current) => {
    //     if (this.selectedPlayers.length < TOTAL_PLAYERS_TO_SELECT) {
    //       this.isNextButtonDisabled = this.selectedCircles.length < 1;
    //       this.isCircleSelectionDisabled = false;
    //     } else {
    //       this.isNextButtonDisabled = false;
    //       this.isCircleSelectionDisabled = true;
    //     }
    //   }
    // );

    reaction(
      () => this.rootStore.courtBookingStore.totalPlayersToSelect,
      (current) => {
        this.isShowRemoveExtraPlayersDialog =
          this.selectedPlayers.length >
          this.rootStore.courtBookingStore.totalPlayersToSelect;

        if (
          this.rootStore.courtBookingStore.totalPlayersInMatch ===
            TOTAL_PLAYERS_IN_MATCH_SINGLES &&
          this.selectedPlayers.length <=
            this.rootStore.courtBookingStore.totalPlayersToSelect
        ) {
          this.selectSinglesCourtType();
        }
      }
    );

    reaction(
      () => ({
        selectedPlayersLength: this.selectedPlayers.length,
        playersToSelect: this.rootStore.courtBookingStore.totalPlayersToSelect,
      }),
      async (current) => {
        this.guestPlayerCount = this.selectedPlayers.filter(
          (player) => player.isGuestPlayer
        ).length;

        if (
          this.selectedPlayers.length ===
          this.rootStore.courtBookingStore.totalPlayersToSelect
        ) {
          this.selectedCircles = [];
          // TODO: REMOVE BELOW CODE (INCLUDING ELSE BLOCK) WHEN SELECT CIRCLE GRAPH IS ENABLED
          this.isNextButtonDisabled = false;
        } else {
          this.isNextButtonDisabled = true;
        }
      }
    );
  }

  toggleCircleSelection = (circleId: number) => {
    if (circleId <= 4) {
      if (this.selectedCircles.includes(circleId)) {
        this.selectedCircles = this.selectedCircles.filter(
          (id) => id !== circleId
        );
      } else {
        this.selectedCircles.push(circleId);
        // Sort the array in ascending order after adding a new circle
        this.selectedCircles.sort((a, b) => a - b);
      }
    }
  };

  // method to add player to selectedPlayersToRemove list to remove from selected players list
  addPlayerToRemoveFromSelected = (player: Player) => {
    const index = this.selectedPlayersToRemove.findIndex(
      (p) => p.userId === player.userId
    );

    if (index === -1) {
      this.selectedPlayersToRemove.push(player);
    } else {
      this.selectedPlayersToRemove = this.selectedPlayersToRemove.filter(
        (p) => p.userId !== player.userId
      );
    }
  };

  selectSinglesCourtType = () => {
    this.rootStore.courtBookingStore.setSelectedCourt(
      this.rootStore.courtBookingStore.courts.find(
        (court) => court.id === CourtId.Singles
      ) ?? this.rootStore.courtBookingStore.courts[0]
    );
  };

  removePlayersFromSelectedPlayersList = () => {
    if (
      this.rootStore.courtBookingStore.totalPlayersInMatch ===
      TOTAL_PLAYERS_IN_MATCH_SINGLES
    ) {
      this.selectSinglesCourtType();
    }

    this.selectedPlayers = this.selectedPlayers.filter(
      (player) =>
        !this.selectedPlayersToRemove.some((p) => p.userId === player.userId)
    );
    this.selectedPlayersToRemove = [];
  };

  setBottomSheetType = (type: PlayerSelectionBottomSheetType) => {
    this.setFilteredName("");
    this.bottomSheetType = type;
  };

  setIsInitialExpanded = (isInitialExpanded: boolean) => {
    this.isInitialExpanded = isInitialExpanded;
  };

  fetchFilteredPlayers = async () => {
    try {
      this.isLoading = true;
      const response = await CourtBookingApi.getPlayersList(
        this.searchPageNum,
        this.filteredName
      );
      if (response.length === 0) {
        this.isStopSearchPagination = true;
        return;
      }

      if (this.searchPlayerListResponse) {
        const mergedPlayers = [...this.searchPlayerListResponse, ...response];
        this.searchPlayerListResponse = removeDuplicates(
          mergedPlayers,
          (player) => player.userId
        );
      } else {
        this.searchPlayerListResponse = response;
      }
    } catch (err) {
      console.log(err);
      this.rootStore.courtBookingStore.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: `Error occurred while fetching players with filter: ${this.filteredName}`,
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.rootStore.courtBookingStore.modalData = undefined),
      };
    } finally {
      this.isLoading = false;
    }
  };

  fetchPlayers = async () => {
    try {
      this.isLoading = true;
      const response = await CourtBookingApi.getPlayersList(this.pageNum, "");
      if (response.length === 0) {
        this.isStopPagination = true;
        return;
      }
      if (this.playersListResponse) {
        const mergedPlayers = [...this.playersListResponse, ...response];
        this.playersListResponse = removeDuplicates(
          mergedPlayers,
          (player) => player.userId
        );
      } else {
        this.playersListResponse = response;
      }
    } catch (err) {
      console.log(err);
      this.rootStore.courtBookingStore.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: `Error occurred while fetching players list`,
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.rootStore.courtBookingStore.modalData = undefined),
      };
    } finally {
      this.isLoading = false;
    }
  };

  getPlayersList = (isExcludingPlayers?: boolean) => {
    let players = this.filteredName
      ? this.searchPlayerListResponse
      : this.playersListResponse;
    const currentUser = this.rootStore.userStore.userModel;

    if (!players) return null;

    // If we're in selection mode, filter out excluded players
    if (!isExcludingPlayers) {
      players = players.filter(
        (player) =>
          !this.excludedPlayers.some((ep) => ep.userId === player.userId)
      );
    }
    // If we're in exclusion mode, filter out selected players
    else {
      players = players.filter(
        (player) =>
          !this.selectedPlayers.some((sp) => sp.userId === player.userId)
      );
    }

    return players.filter(
      (player) => player.userId.toString() !== currentUser?.userId?.toString()
    );
  };

  increasePageNum = () => {
    if (this.filteredName && !this.isStopSearchPagination) {
      this.searchPageNum += 1;
    } else if (!this.filteredName && !this.isStopPagination) {
      this.pageNum += 1;
    }
  };

  fetchCirclePlayers = async (minRating: number, maxRating: number) => {
    this.isCirclePlayersLoading = true;
    try {
      const response = await CourtBookingApi.getCirclePlayersList(
        minRating,
        maxRating
      );
      this.circlePlayerResponse = response;
      const allPlayers: CirclePlayer[] = [
        ...response.innerCirclePlayers.players,
        ...response.firstCirclePlayers.players,
        ...response.secondCirclePlayersWithPbpRating.players,
        ...response.secondCirclePlayersWithUltraRating.players,
        ...response.thirdCirclePlayersWithPbpRating.players,
        ...response.thirdCirclePlayersWithUltraRating.players,
      ];
      if (allPlayers.length > 8) {
        this.initialCirclePlayersList = allPlayers.slice(0, 8);
      } else {
        this.initialCirclePlayersList = allPlayers;
      }
    } catch (err) {
      console.log(err);
      this.rootStore.courtBookingStore.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: "Error occurred while fetching circle players list",
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.rootStore.courtBookingStore.modalData = undefined),
      };
    } finally {
      this.isCirclePlayersLoading = false;
    }
  };

  setFilteredName = (name: string) => {
    this.filteredName = name;
  };

  togglePlayerSelection = (player: Player) => {
    const playerIndex = this.selectedPlayers.findIndex(
      (p) => p.userId === player.userId
    );

    if (playerIndex > -1) {
      // Remove player if already selected
      this.selectedPlayers = this.selectedPlayers.filter(
        (p) => p.userId !== player.userId
      );
    } else {
      // Add player to selection
      if (
        this.selectedPlayers.length ===
        this.rootStore.courtBookingStore.totalPlayersToSelect
      ) {
        this.rootStore.courtBookingStore.modalData = {
          visible: true,
          title: Strings.WARNING,
          children: "Max limit reached. Please remove player to continue",
          btnText: Strings.OK,
          btnVariant: "outline-danger",
          onClose: () =>
            (this.rootStore.courtBookingStore.modalData = undefined),
        };
      } else {
        this.selectedPlayers = [...this.selectedPlayers, player];
      }
    }
  };
  togglePlayerExclusion = (player: Player) => {
    const playerIndex = this.excludedPlayers.findIndex(
      (p) => p.userId === player.userId
    );

    if (playerIndex > -1) {
      // Remove player if already excluded
      this.excludedPlayers = this.excludedPlayers.filter(
        (p) => p.userId !== player.userId
      );
    } else {
      // Add player to exclusion
      this.excludedPlayers = [...this.excludedPlayers, player];
    }
  };

  isPlayerSelected = (playeruserId: number): boolean => {
    return this.selectedPlayers.some(
      (player) => player.userId === playeruserId
    );
  };

  isPlayerExcluded = (playeruserId: number): boolean => {
    return this.excludedPlayers.some(
      (player) => player.userId === playeruserId
    );
  };

  clearSelectedPlayers = () => {
    this.selectedPlayers = [];
  };

  expandSelectCircle = () => {
    this.rootStore.sectionStore.onExpandSelectCircle();
  };

  // Create a method that takes userModel as a parameter
  adjustAndGetSelectedPlayersList = () => {
    // Create a copy of the current selected players list
    let adjustedPlayersList = this.selectedPlayers.map((pl) => {
      return {
        ...pl,
        totalAmount: this.rootStore.payemntStore.getPlayerAmount(pl),
      };
    });
    const userModel = this.rootStore.userStore.userModel;

    // Add current user to the beginning if available
    if (userModel) {
      const currentUser: Player = {
        userId: parseInt(userModel.userId, 10) ?? -1,
        name: userModel.name,
        userImageUrl: userModel.userImageUrl ?? "",
        email: userModel.email,
        clubRank: 0,
        matchesWon: 0,
        userRating: 0,
        count: 0,
        lastMatchDate: "",
        totalAmount: 0,
      };

      // Check if current user is already in the list
      const isCurrentUserSelected = adjustedPlayersList.some(
        (player) => player.userId === currentUser.userId
      );

      // If not already in the list, add to the beginning
      if (!isCurrentUserSelected) {
        adjustedPlayersList = [
          {
            ...currentUser,
            totalAmount:
              this.rootStore.payemntStore.getPlayerAmount(currentUser) ?? 0,
          },
          ...adjustedPlayersList,
        ];
      }
    }

    // // Fill remaining slots with guest players if needed
    // const remainingSlots = TOTAL_PLAYERS_IN_MATCH - adjustedPlayersList.length;
    // if (remainingSlots > 0) {
    //   // Add guest players to fill the remaining slots
    //   const guestPlayersToAdd = GUEST_PLAYERS.slice(0, remainingSlots);
    //   adjustedPlayersList = [...adjustedPlayersList, ...guestPlayersToAdd];
    // }

    return adjustedPlayersList;
  };

  incrementGuestCount(): void {
    // Find first guest player that's not already in selectedPlayers
    const availableGuestPlayer = GUEST_PLAYERS.find(
      (guestPlayer) =>
        !this.selectedPlayers.some(
          (selectedPlayer) => selectedPlayer.userId === guestPlayer.userId
        )
    );

    // If we found an available guest player, add them
    if (
      availableGuestPlayer &&
      this.selectedPlayers.length <
        this.rootStore.courtBookingStore.totalPlayersToSelect
    ) {
      this.togglePlayerSelection(availableGuestPlayer);
    }
  }
  decrementGuestCount(): void {
    const guestPlayer = GUEST_PLAYERS[this.guestPlayerCount - 1];
    this.togglePlayerSelection(guestPlayer);
  }

  checkForCourtTypeAndPlayersCount = () => {
    if (
      this.rootStore.courtBookingStore.selectedCourt.id === CourtId.Singles &&
      this.selectedPlayers.length >
        this.rootStore.courtBookingStore.totalPlayersToSelect
    ) {
    }
  };
}
